import { Languages } from '@wicadu/langscope'

import { PagesId } from '@ts-enums/PagesId'
import { ContentType } from '@ts-types/ArticleSection'
import { Translations } from '@ts-types/Translations'
import { getUrlByPageId } from '@utils/getUrlByPageId'

const globalTranslations: Translations = {
  es: {
    seo_title: 'Editar productos',
    seo_description: 'Aprende cómo editar productos existentes en tu tienda paso a paso. Descubre cómo actualizar nombres, categorías, descripciones y otros detalles clave para mantener tu catálogo siempre actualizado.',
    seo_keywords:
      'editar productos, actualizar productos, cambiar detalles del producto, modificar información de productos, actualizar inventario tienda, editar productos wicadu, actualizar productos wicadu, wicadu, guia, gestionar catálogo productos, actualizar descripciones productos, editar precios productos, modificar nombres productos',
    page_id: PagesId.UpdateItems,
    breadcrumbs: {
      home: 'Inicio',
      items_home: 'Productos e inventario',
      update_items: 'Editar productos',
    },
    article: {
      title: 'Editar productos',
      intended: [
        `<strong>Propietarios</strong> o <strong>Administradores</strong> con acceso completo para gestionar <em>inventario</em> y <em>productos</em> dentro del <a href=${process.env.STORES_WEBAPP_HOST} title='Asistente de wicadu'>Asistente de wicadu</a>.`,
        `<strong>Vendedores</strong> o <strong>Abastecedor</strong> con permisos para <em>crear productos</em>, <em>actualizar características</em> y <em>asignar precios</em>.`,

      ],
      about: {
        title: 'Acerca de editar productos',
        content_type: ContentType.Paragraph,
        content: [
          '<strong>Editar productos</strong> en tu tienda te permite <em>mantener actualizada la información de tu inventario</em>. Ya sea para <em>corregir errores</em>, <em>actualizar descripciones</em> o <em>reorganizar productos</em>, esta funcionalidad te ayuda a ofrecer siempre información <b>clara y precisa</b> a tus clientes.',
          'Realizar cambios rápidos en productos existentes también <strong>facilita la gestión del inventario</strong>, <em>mejora la experiencia de compra</em> y permite <strong>adaptarte rápidamente</strong> a cambios en tus ofertas.'
        ],
      },
      before_begin: {
        title: 'Antes de empezar',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Antes de editar un producto, asegúrate de haber considerado lo siguiente:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Evita realizar <strong>modificaciones constantes en productos ya publicados</strong>, ya que puede generar confusión en tus clientes y afectar la visibilidad del producto.'
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Las actualizaciones <strong>se reflejan en todos los canales de venta integrados</strong> (tienda en línea, redes sociales, marketplaces).'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Nota</strong>: Los cambios que realices en un producto <em>no afectarán</em> a las órdenes en proceso ni a las órdenes completadas</strong>. Esas órdenes conservarán la información original del producto.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Asegúrate de tener permisos para procesar devoluciones, cambios o reembolsos. Consulta con tu administrador si no estás seguro.',
                ],
              },
            ],
          },

          {
            content_type: ContentType.Paragraph,
            content: [
              'Seguir estas recomendaciones antes de editar productos te ayudará a <strong>mantener una gestión organizada</strong> de tu catálogo, <em>evitar errores</em> en pedidos y <strong>ofrecer una mejor experiencia de compra</strong> a tus clientes en tu <strong>tienda en línea</strong>.',
            ],
          },
        ],
      },
      how_to: {
        title: 'Pasos para editar un producto',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Ir al módulo de Inventario: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Accede a la funcionalidad de <em>Inventario</em> dentro del <strong>Asistente de wicadu</strong>.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Buscar el producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Usa el buscador para encontrar el producto que deseas editar. Puedes buscar por nombre o código SKU.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Seleccionar el producto: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Selecciona el producto en la lista y haz clic en el <strong>ícono junto al nombre</strong>, en la parte superior derecha, para entrar en el <em>modo de edición</em>',
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Editar los campos necesarios: '],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Nombre del producto</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Descripción</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Categoría o tipo</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Características (dimensiones, materiales, etc.)</strong>',
                    ],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Guardar los cambios: '],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Haz clic en <strong>Guardar</strong> para aplicar los cambios. Los detalles actualizados estarán visibles inmediatamente para tus clientes.'
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Siguientes pasos',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Ahora que sabes cómo editar productos, considera las siguientes acciones:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Actualizar imágenes del producto: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // ARTICLE IDEA
                      'Actualiza las imágenes del producto si ha cambiado su presentación, empaque o diseño, para mantener una representación visual clara y precisa.'
                      // `Actualiza las imágenes del producto si ha cambiado su presentación, empaque o diseño, para mantener una representación visual clara y precisa. Consulta
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.Spanish)} title="Guía para agregar imágenes a un producto">Cómo actualizar imágenes de un producto</a>. para más información`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Revisar características: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Asegúrate de que las características (como tallas o colores) sigan siendo válidas después de cualquier edición.'
                      // `Asegúrate de que las variantes (como tallas o colores) sigan siendo válidas después de cualquier edición. Aprende más sobre
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.Spanish)} title="Guía para editar y actualizar productos">cómo editar y actualizar productos</a>.`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problemas Comunes',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>No puedes editar el producto</strong>: Asegúrate de tener permisos adecuados para editar productos.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>El botón "Continuar" está deshabilitado</strong>: Asegúrate de que todos los campos esten completos y sin errores de validación.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Los cambios no se reflejan en la tienda</strong>: Intenta actualizar la página. Si el problema persiste, contacta al equipo de <a href=${getUrlByPageId(PagesId.ContactUs, Languages.Spanish)} title="soporte de wicadu">Soporte de wicadu</a> para asistencia.`,
            ],
          },
        ],
      },
    },
  },
  en: {
    seo_title: 'Edit products',
    seo_description:
      'Learn how to edit product details in your online store. Step-by-step guide to update names, descriptions, specifications, and inventory settings in wicadu.',
    seo_keywords:
      'edit products, update product details, manage products, online store inventory, change product description, wicadu product management',
    page_id: PagesId.UpdateItems,
    breadcrumbs: {
      home: 'Home',
      items_home: 'Products and inventory',
      update_items: 'Edit products',
    },
    article: {
      title: 'Edit products',
      intended: [
        `<strong>Store Owners</strong> or <strong>Admins</strong> with full access to manage <em>inventory</em> and <em>products</em> within the <a href=${process.env.STORES_WEBAPP_HOST}>wicadu Assistant</a>.`,
        '<strong>Buyers</strong> or <strong>Suppliers</strong> with permissions to <em>edit products</em>, <em>update features</em>, and <em>adjust prices</em>.',
      ],
      about: {
        title: 'About editing products',
        content_type: ContentType.Paragraph,
        content: [
          `<strong>Editing products</strong> in your store allows you to <em>keep your inventory information up to date</em>. Whether you are <em>fixing mistakes</em>, <em>updating descriptions</em>, or <em>reorganizing your inventory</em>, this functionality helps you consistently present <em></em> information to your customers.`,
          'Making quick changes to existing products also <em>simplifies inventory management</em>, <em>enhances the shopping experience</em>, and allows you to <em>quickly adapt to changes</em> in your offerings.',
        ],
      },
      before_begin: {
        title: 'Before you begin',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Before editing a product, make sure you have considered the following:',
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      "Avoid making <strong>frequent changes to already published products</strong>, as this may confuse your customers and affect the product's visibility."
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<em>Updates will be reflected across all integrated sales channels</em> (online store, social media, marketplaces).'
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      "<strong>Note</strong>: Any changes you make to a product will not affect orders that are currently in progress or already completed. Those orders will retain the product's original information",
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  "Make sure you have the proper permissions to handle returns, exchanges, or refunds. Check with your administrator if you are unsure."
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Following these recommendations before editing products will help you <strong>maintain an organized catalog</strong>, avoid <em>errors in orders</em>, and <strong>provide a better shopping experience</strong> for your customers in your <strong>online store</strong>.'
            ],
          },
        ],
      },
      how_to: {
        title: 'Steps to edit a product',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Go to the Inventory module:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Access the <em>Inventory</em> section within the <strong>wicadu Assistant</strong>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Find the product:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Use the search bar to locate the product you want to edit. You can search by name or SKU code.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Select the product:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click on the product in the list and then click the <strong>icon next to the product name</strong> (top right) to enter <em>edit mode</em>.'
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Edit the necessary fields:'],
              },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Product name</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Description</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Category or type</strong>',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      '<strong>Specifications (dimensions, materials, etc.)</strong>',
                    ],
                  },
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              {
                content_type: ContentType.Title3,
                class: 'bold',
                content: ['Save your changes:'],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Click <strong>Save</strong> to apply the updates. The updated product details will be immediately visible to your customers.',
                ],
              },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Next steps',
        content_type: ContentType.Fragment,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              'Now that you know how to edit products, consider the following actions:'
            ],
          },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Update product images: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      // ARTICLE IDEA
                      'Update the product images if its presentation, packaging, or design has changed, to maintain a clear and accurate visual representation.',
                      // `Update the product images if its presentation, packaging, or design has changed, to maintain a clear and accurate visual representation. Consulta
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.English)} title="Guía para agregar imágenes a un producto">Cómo actualizar imágenes de un producto</a>. para más información`,
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  {
                    content_type: ContentType.Title3,
                    class: 'bold',
                    content: ['Review specifications: '],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Make sure that the specifications (such as sizes or colors) are still valid after any edits.'
                      // `Asegúrate de que las variantes (como tallas o colores) sigan siendo válidas después de cualquier edición. Aprende más sobre
                      // <a href=${getUrlByPageId(PagesId.ItemsHome, Languages.English)} title="Guía para editar y actualizar productos">cómo editar y actualizar productos</a>.`,
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Common Issues',
        content_type: ContentType.UnorderedList,
        content: [
          {
            content_type: ContentType.Paragraph,
            content: [
              "<strong>You can't edit the product</strong>: Make sure you have the proper permissions to edit products.",
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              '<strong>The "Continue" button is disabled</strong>: Ensure all fields are filled out and free of validation errors.',
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              `<strong>Changes are not showing in the store</strong>: Try refreshing the page. If the issue persists, contact the <a href=${getUrlByPageId(PagesId.ContactUs, Languages.English)} title="wicadu Support">wicadu Support</a> team for assistance.`
            ],
          },
        ],
      },
    },
  },
  fr: {
    seo_title: 'Modifier des produits',
    seo_description:
      'Découvrez comment mettre à jour les détails, attributs et informations principales de vos produits dans wicadu.',
    seo_keywords:
      'modifier produit, mettre à jour produit, changer détails produit, inventaire wicadu, catalogue produit',
    page_id: PagesId.UpdateItems,
    breadcrumbs: {
      home: 'Accueil',
      items_home: 'Produits et inventaire',
      update_items: 'Modifier des produits',
    },
    article: {
      title: 'Modifier des produits',
      intended: [
        `<strong>Propriétaires</strong> et <strong>administrateurs</strong> qui gèrent le <em>catalogue</em> depuis l’<a href=${process.env.STORES_WEBAPP_HOST}>assistant wicadu</a>.`,
        `<strong>Vendeurs</strong> autorisés à <em>mettre à jour les informations produit</em> et à maintenir leur exactitude.`,
      ],
      about: {
        title: 'À propos de la modification des produits',
        content_type: ContentType.Paragraph,
        content: [
          'Mettre à jour un <strong>produit</strong> vous permet de corriger des informations, d’ajuster votre <strong>catalogue</strong> et de garder votre offre alignée avec la réalité de votre activité. C’est une étape importante pour maintenir des <strong>produits</strong> cohérents, bien décrits et prêts à être vendus.',
          'Une fiche produit bien entretenue améliore la qualité des ventes, réduit les erreurs opérationnelles et aide vos clients à mieux comprendre ce que vous proposez. Elle vous permet aussi de garder une cohérence entre la description, les <strong>images</strong>, le <strong>prix</strong> et la disponibilité en <strong>stock</strong>.',
        ],
      },
      before_begin: {
        title: 'Avant de commencer',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Avant de modifier un produit, assurez-vous d’avoir pris en compte les éléments suivants :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Évitez d’effectuer des <strong>modifications fréquentes sur des produits déjà publiés</strong>, car cela peut créer de la confusion chez vos clients et affecter la visibilité du produit.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Fragment,
                content: [
                  {
                    content_type: ContentType.Paragraph,
                    content: [
                      'Les mises à jour <strong>seront reflétées sur tous les canaux de vente intégrés</strong> (boutique en ligne, réseaux sociaux, marketplaces).',
                    ],
                  },
                  {
                    content_type: ContentType.Paragraph,
                    class: 'msg-alert',
                    content: [
                      '<strong>Remarque</strong> : les changements que vous apportez à un produit <em>n’affecteront pas</em> les commandes en cours ni les commandes déjà terminées. Ces commandes conserveront les informations originales du produit.',
                    ],
                  },
                ],
              },
              {
                content_type: ContentType.Paragraph,
                content: [
                  'Assurez-vous d’avoir les autorisations nécessaires pour traiter des retours, des échanges ou des remboursements. En cas de doute, vérifiez avec votre administrateur.',
                ],
              },
            ],
          },
          {
            content_type: ContentType.Paragraph,
            content: [
              'Suivre ces recommandations avant de modifier des produits vous aidera à <strong>maintenir un catalogue organisé</strong>, à éviter les <em>erreurs dans les commandes</em> et à <strong>offrir une meilleure expérience d’achat</strong> à vos clients dans votre <strong>boutique en ligne</strong>.',
            ],
          },
        ],
      },
      how_to: {
        title: 'Étapes pour modifier un produit',
        content_type: ContentType.OrderedList,
        content: [
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Aller au module Inventaire : '] },
              { content_type: ContentType.Paragraph, content: ['Accédez à la fonctionnalité <em>Inventaire</em> dans l’<strong>assistant wicadu</strong>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Rechercher le produit : '] },
              { content_type: ContentType.Paragraph, content: ['Utilisez la barre de recherche pour localiser le produit que vous souhaitez modifier. Vous pouvez rechercher par nom ou par code SKU.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Sélectionner le produit : '] },
              { content_type: ContentType.Paragraph, content: ['Cliquez sur le produit dans la liste, puis cliquez sur <strong>l’icône à côté du nom du produit</strong> (en haut à droite) pour entrer en <em>mode d’édition</em>.'] },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Modifier les champs nécessaires : '] },
              {
                content_type: ContentType.UnorderedList,
                content: [
                  { content_type: ContentType.Paragraph, content: ['<strong>Nom du produit</strong>'] },
                  { content_type: ContentType.Paragraph, content: ['<strong>Description</strong>'] },
                  { content_type: ContentType.Paragraph, content: ['<strong>Catégorie ou type</strong>'] },
                  { content_type: ContentType.Paragraph, content: ['<strong>Spécifications (dimensions, matériaux, etc.)</strong>'] },
                ],
              },
            ],
          },
          {
            content_type: ContentType.ListItem,
            content: [
              { content_type: ContentType.Title3, class: 'bold', content: ['Enregistrer les changements : '] },
              { content_type: ContentType.Paragraph, content: ['Cliquez sur <strong>Enregistrer</strong> pour appliquer les mises à jour. Les détails mis à jour du produit seront immédiatement visibles par vos clients.'] },
            ],
          },
        ],
      },
      next_steps: {
        title: 'Étapes suivantes',
        content_type: ContentType.Fragment,
        content: [
          { content_type: ContentType.Paragraph, content: ['Maintenant que vous savez comment modifier des produits, envisagez les actions suivantes :'] },
          {
            content_type: ContentType.UnorderedList,
            content: [
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Mettre à jour les images : '] },
                  { content_type: ContentType.Paragraph, content: ['Mettez à jour les images du produit si sa présentation, son emballage ou son design ont changé, afin de conserver une représentation visuelle claire et précise.'] },
                ],
              },
              {
                content_type: ContentType.ListItem,
                content: [
                  { content_type: ContentType.Title3, class: 'bold', content: ['Vérifier les spécifications : '] },
                  { content_type: ContentType.Paragraph, content: ['Assurez-vous que les spécifications (comme les tailles ou les couleurs) restent valides après toute modification.'] },
                ],
              },
            ],
          },
        ],
      },
      common_issues: {
        title: 'Problèmes fréquents',
        content_type: ContentType.UnorderedList,
        content: [
          { content_type: ContentType.Paragraph, content: ['<strong>Vous ne pouvez pas modifier le produit</strong> : assurez-vous de disposer des autorisations nécessaires pour modifier des produits.'] },
          { content_type: ContentType.Paragraph, content: ['<strong>Le bouton "Continuer" est désactivé</strong> : assurez-vous que tous les champs sont remplis et qu’il n’y a aucune erreur de validation.'] },
          { content_type: ContentType.Paragraph, content: [`<strong>Les changements n’apparaissent pas dans la boutique</strong> : essayez d’actualiser la page. Si le problème persiste, contactez l’équipe de <a href=${getUrlByPageId(PagesId.ContactUs, Languages.French)} title="support wicadu">support wicadu</a> pour obtenir de l’aide.`] },
        ],
      },
    },
  },
  pt: {},
}


export default globalTranslations
